import React, { useEffect } from 'react';
import {
  View,
  StyleSheet,
  Text,
  TouchableOpacity,
  ScrollView,
} from 'react-native';
import { useMediaQuery } from 'react-responsive';

import { SELECTED_METHOD } from 'shared/constants/ekyc';
import Button from 'shared/modules/common/components/atoms/Button';
import Icon from 'shared/modules/common/components/atoms/Icon';
import SimpleRadio from 'shared/modules/common/components/atoms/SimpleRadio';

const SelectMethodStage = (props: {
  onNextButtonPress: () => void;
  selectedMethod: number;
  onSelectMethod: (selected: Number) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}) => {
  const {
    onNextButtonPress,
    selectedMethod,
    onSelectMethod,
    onFocus,
    onBlur,
  } = props;
  const isBigScreen = useMediaQuery({ minWidth: 768 });

  useEffect(() => {
    onFocus?.();

    return () => onBlur?.();
  }, []);

  // Dynamic style of the option based on selection
  const getOptionViewStyle = (selected: boolean) => {
    if (selected) {
      return styles.optionViewSelected;
    } else {
      return styles.optionViewUnselected;
    }
  };

  return (
    <View style={styles.root}>
      <ScrollView style={styles.scrollView}>
        <Text style={styles.mainTitle}>{`撮影方法の選択`} </Text>

        {/* Option 1: Paidy App */}
        <Text style={styles.subtitle}>{`アプリをお持ちの場合`} </Text>
        <TouchableOpacity
          onPress={() => onSelectMethod(SELECTED_METHOD.MOBILE_APP)}
        >
          <View
            style={getOptionViewStyle(
              selectedMethod === SELECTED_METHOD.MOBILE_APP
            )}
          >
            <View style={styles.optionView}>
              <View style={styles.firstElementInRow}>
                <Icon name="paidy-bw" size="24" style={{ marginRight: 9 }} />
                <Text style={styles.optionText}>{`アプリで撮影`}</Text>
              </View>
              <View style={styles.lastElementInRow}>
                <SimpleRadio
                  checked={selectedMethod === SELECTED_METHOD.MOBILE_APP}
                />
              </View>
            </View>
            <View style={styles.dottedStyle} />
            <Text
              style={styles.contentText}
            >{`最も精度の高い撮影方法です。\nスマートフォンにアプリをダウンロード済み\nの方は、ぜひこちらを選択してください。`}</Text>
          </View>
        </TouchableOpacity>

        {/* Option 2: Mobile Web */}
        <Text style={styles.subtitle}>{`アプリをお持ちでない場合`} </Text>
        <TouchableOpacity
          onPress={() => onSelectMethod(SELECTED_METHOD.MOBILE_WEB)}
        >
          <View
            style={getOptionViewStyle(
              selectedMethod === SELECTED_METHOD.MOBILE_WEB
            )}
          >
            <View style={styles.optionView}>
              <View>
                <View style={styles.firstElementInRow}>
                  <Icon name="mobile" size="24" style={{ marginRight: 9 }} />
                  <Text
                    style={styles.optionText}
                  >{`モバイルサイトで撮影`}</Text>
                </View>
              </View>
              <View style={styles.lastElementInRow}>
                <SimpleRadio
                  checked={selectedMethod === SELECTED_METHOD.MOBILE_WEB}
                />
              </View>
            </View>

            {isBigScreen && (
              <>
                <View style={styles.dottedStyle} />
                <Text style={styles.contentText}>
                  {`パソコンよりも精度の高い方法です。`}
                </Text>
              </>
            )}
          </View>
        </TouchableOpacity>

        {/* Option 3: Desktop */}
        {isBigScreen && (
          <TouchableOpacity
            onPress={() => onSelectMethod(SELECTED_METHOD.DESKTOP)}
          >
            <View
              style={getOptionViewStyle(
                selectedMethod === SELECTED_METHOD.DESKTOP
              )}
            >
              <View style={styles.optionView}>
                <View style={styles.firstElementInRow}>
                  <Icon name="desktop" size="24" style={{ marginRight: 9 }} />
                  <Text style={styles.optionText}>{`パソコンで撮影`}</Text>
                </View>
                <View style={styles.lastElementInRow}>
                  <SimpleRadio
                    checked={selectedMethod === SELECTED_METHOD.DESKTOP}
                  />
                </View>
              </View>
            </View>
          </TouchableOpacity>
        )}
      </ScrollView>

      <View style={styles.buttonContainer}>
        <Button
          variant="primary"
          title="次へ"
          onPress={onNextButtonPress}
          id="ekyc-onboarding-select-method-stage-next-button"
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  scrollView: {
    flexGrow: 1,
    marginBottom: 24,
  },
  mainTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 24,
    color: '#000',
  },
  subtitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 24,
    color: '#000',
  },
  optionViewSelected: {
    padding: 24,
    borderWidth: 1.5,
    borderColor: '#E30E80',
    borderRadius: 16,
    backgroundColor: '#fff',
    marginBottom: 24,
  },
  optionViewUnselected: {
    padding: 24,
    borderWidth: 1.5,
    borderColor: '#fff',
    borderRadius: 16,
    backgroundColor: '#fff',
    marginBottom: 24,
  },
  optionView: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  firstElementInRow: {
    flexDirection: 'row',
    flex: 8,
    alignItems: 'center',
  },
  lastElementInRow: {
    flex: 5,
    alignItems: 'flex-end',
  },
  optionText: {
    fontSize: 13,
    lineHeight: 16,
    fontWeight: '600',
  },
  contentText: {
    fontSize: 13,
    lineHeight: 16,
    color: '#2A2E32',
    marginTop: 9,
  },
  buttonContainer: {
    flex: 1,
    flexDirection: 'row',
    flexGrow: 1,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  dottedStyle: {
    height: 1,
    backgroundColor: '#e1e1e1',
    marginVertical: 16,
  },
});

export default SelectMethodStage;
